import React, { useMemo, useState, useCallback } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import InputLabel from '../../../../../../components/form/InputLabel';
import Button from '../../../../../../components/button';
import { useTranslation } from 'react-i18next';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { usePullGoalAvailableGoalsQuery } from '../../../../__generated__/graphql';
import ServerError from '../../../../../../components/ServerError';
import { mapGoalOption } from '../useGoalSelectOptions';
import { SelectSingleGoal } from '../GoalSidebar/SelectSingleGoal';
import { usePullObjectives } from '../GoalSidebar/useObjectiveMutations';
import { GoalId } from '../types';
import { useSelectedGoal } from '../useSelectedGoal';
import { useUserInfo } from '../../../../context/UserInfo';
import { GoalOption } from '../useGoalSelectOptions';
import SingleFilter from '../../../../../../components/SelectFilters/SingleFilter';
import { toObjectiveIdInput } from '../mappings';
import '../GoalsModal.css';

type PullGoalFilterMode = 'companyAndTeam' | 'allExceptMy' | 'all';

const PULL_GOAL_FILTER_MODES: readonly PullGoalFilterMode[] = [
  'companyAndTeam',
  'allExceptMy',
  'all',
];
const isPullGoalFilterMode = (v: string | null): v is PullGoalFilterMode =>
  v !== null && (v === 'companyAndTeam' || v === 'allExceptMy' || v === 'all');

type PullGoalModalProps = {
  visible: boolean;
  onClose: () => void;
};

const i18prefix = 'performanceReview.goals2.pullGoal';

const filterGoalsByMode = (
  goals: GoalOption[],
  mode: PullGoalFilterMode,
  currentUserId: string,
): GoalOption[] => {
  switch (mode) {
    case 'companyAndTeam':
      return goals.filter(
        (g) => g.goalType === 'company' || g.goalType === 'team',
      );
    case 'allExceptMy':
      return goals.filter(
        (g) => !g.goalEmployee || g.goalEmployee.id !== currentUserId,
      );
    case 'all':
    default:
      return goals;
  }
};

export const PullGoalModal: React.FC<PullGoalModalProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const errorHandler = useMutationErrorHandler();
  const { currentPeriodId } = useGoalsContext();
  const { id: currentUserId } = useUserInfo();
  const [selectedGoalId, setSelectedGoalId] = useState<GoalId | null>(null);
  const [setAsParent, setSetAsParent] = useState<boolean>(true);
  const [filterMode, setFilterMode] =
    useState<PullGoalFilterMode>('companyAndTeam');
  const [pullObjectives, { loading: isPulling }] = usePullObjectives();
  const { setSelectedGoalId: openGoalSidebar } = useSelectedGoal();
  const { data, error, loading } = usePullGoalAvailableGoalsQuery({
    variables: {
      periodId: currentPeriodId,
    },
    skip: !visible,
  });

  const allGoalsForPulling = useMemo(() => {
    if (!data?.allObjectives) {
      return [];
    }
    return data.allObjectives.map(mapGoalOption);
  }, [data?.allObjectives]);

  const availableGoalsForPulling = useMemo(
    () => filterGoalsByMode(allGoalsForPulling, filterMode, currentUserId),
    [allGoalsForPulling, filterMode, currentUserId],
  );

  const filterModeOptions = useMemo(
    () =>
      PULL_GOAL_FILTER_MODES.map((mode) => ({
        label: t(`${i18prefix}.filter.${mode}`),
        value: mode,
      })),
    [t],
  );

  const handleFilterModeChange = useCallback(
    (value: string | null) => {
      const newMode: PullGoalFilterMode = isPullGoalFilterMode(value)
        ? value
        : 'companyAndTeam';
      setFilterMode(newMode);
      setSelectedGoalId((prev) => {
        if (!prev) return null;
        const stillAvailable = filterGoalsByMode(
          allGoalsForPulling,
          newMode,
          currentUserId,
        ).some((g) => g.value === prev.id);
        return stillAvailable ? prev : null;
      });
    },
    [allGoalsForPulling, currentUserId],
  );

  const handleClose = useCallback(() => {
    setSelectedGoalId(null);
    setSetAsParent(true);
    setFilterMode('companyAndTeam');
    onClose();
  }, [onClose]);

  const renderGoalsModalError = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{t(`${i18prefix}.title`)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ServerError />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          label={t(`${i18prefix}.cancel`)}
          onClick={handleClose}
        />
      </Modal.Footer>
    </>
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedGoalId) {
        return;
      }
      pullObjectives({
        variables: {
          input: {
            objectiveIds: [toObjectiveIdInput(selectedGoalId)],
            setAsParent,
          },
        },
      })
        .then((result) => {
          const createdRecordIds =
            result.data?.objectives?.pullObjectives?.createdRecordIds;
          if (createdRecordIds && createdRecordIds.length === 1) {
            const createdGoalId = createdRecordIds[0];
            // it is redirect
            openGoalSidebar({ id: createdGoalId, type: 'employee' });
          }
          handleClose();
        })
        .catch(errorHandler);
    },
    [
      selectedGoalId,
      setAsParent,
      pullObjectives,
      handleClose,
      openGoalSidebar,
      errorHandler,
    ],
  );

  if (!visible) {
    return null;
  }

  return (
    <Modal
      show={visible}
      onHide={handleClose}
      size="lg"
      centered
      backdropClassName="goals-modal-backdrop"
      contentClassName="goals-modal-content border">
      {error ? (
        renderGoalsModalError()
      ) : (
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <div>
              <Modal.Title>{t(`${i18prefix}.title`)}</Modal.Title>
              <div>{t(`${i18prefix}.subtitle`)}</div>
            </div>
          </Modal.Header>

          <Modal.Body className="p-4">
            <FormGroup>
              <InputLabel label={t(`${i18prefix}.filterLabel`)} />
              <SingleFilter
                placeholder={t(`${i18prefix}.filterPlaceholder`)}
                options={filterModeOptions}
                value={filterMode}
                onChange={handleFilterModeChange}
                isClearable={false}
              />
            </FormGroup>
            <FormGroup>
              <InputLabel label={t(`${i18prefix}.selectGoalLabel`)} />
              <SelectSingleGoal
                value={selectedGoalId}
                options={availableGoalsForPulling}
                placeholder={t(`${i18prefix}.selectGoalPlaceholder`)}
                heightMode="small"
                isDisabled={loading}
                onChange={setSelectedGoalId}
              />
            </FormGroup>
            <FormGroup>
              <Form.Check
                type="checkbox"
                id="setAsParent"
                label={t(`${i18prefix}.setAsParent`)}
                checked={setAsParent}
                onChange={(e) => setSetAsParent(e.target.checked)}
              />
              <Form.Text className="text-muted">
                {setAsParent
                  ? t(`${i18prefix}.copyDescriptionWithParent`)
                  : t(`${i18prefix}.copyDescriptionWithoutParent`)}
              </Form.Text>
            </FormGroup>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              label={t(`${i18prefix}.cancel`)}
              onClick={handleClose}
              disabled={isPulling}
            />
            <Button
              variant="primary"
              label={t(`${i18prefix}.pullGoal`)}
              type="submit"
              isProcessing={isPulling}
              disabled={isPulling || loading || !selectedGoalId}
            />
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
};
